import { useState, useCallback } from 'react';
import { MediaPickerModal } from './MediaLibrary/MediaPickerModal';
import { Button } from './ui/Button';
import { trpc } from '../lib/trpc';
import { FormSection } from './ui/FormSection';
import type { Media } from '@wineryxplorer/shared';

export interface EventMediaUploadProps {
  venueId: string;
  photoIds: string[];
  videoIds: string[];
  onPhotosChange: (photoIds: string[]) => void;
  onVideosChange: (videoIds: string[]) => void;
  maxPhotos?: number;
  maxVideos?: number;
  className?: string;
}

export const EventMediaUpload = ({
  venueId,
  photoIds,
  videoIds,
  onPhotosChange,
  onVideosChange,
  maxPhotos = 10,
  maxVideos = 5,
  className = '',
}: EventMediaUploadProps) => {
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Fetch media to display thumbnails
  const { data: mediaList } = trpc.media.getByIds.useQuery(
    { ids: [...photoIds, ...videoIds] },
    { enabled: photoIds.length > 0 || videoIds.length > 0 }
  );

  const media = (mediaList ?? []) as Media[];

  const handleRemovePhoto = useCallback((photoId: string) => {
    onPhotosChange(photoIds.filter(id => id !== photoId));
  }, [photoIds, onPhotosChange]);

  const handleRemoveVideo = useCallback((videoId: string) => {
    onVideosChange(videoIds.filter(id => id !== videoId));
  }, [videoIds, onVideosChange]);

  return (
    <div className={className}>
      {/* Photos Section */}
      <FormSection
        title="Event Photos"
        titleAs="h3"
        // The wrapper div hides these sections from the form's `:first-child`
        // rule, and media never leads a form — so the separator is explicit.
        divider
        description={maxPhotos ? `${photoIds.length} of ${maxPhotos} photos used.` : undefined}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPhotoModal(true)}
            disabled={maxPhotos ? photoIds.length >= maxPhotos : false}
          >
            {photoIds.length > 0 ? 'Manage Photos' : 'Add Photos'}
          </Button>
        }
      >
        {/* Photo Thumbnails */}
        {photoIds.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {photoIds.map((photoId, index) => {
              const mediaItem = media.find(m => m.id === photoId);
              return (
                <div key={photoId} className="relative group">
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                    {mediaItem?.cloudinaryUrl ? (
                      <img
                        src={mediaItem.cloudinaryUrl}
                        alt={mediaItem.caption || 'Event photo'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  {/* Order indicator */}
                  <div className="absolute top-2 left-2 w-6 h-6 bg-primary-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {index + 1}
                  </div>
                  
                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => handleRemovePhoto(photoId)}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Remove photo"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm text-gray-600">No photos added yet</p>
            <p className="text-xs text-gray-500 mt-1">Click "Add Photos" to select images</p>
          </div>
        )}
      </FormSection>

      {/* Videos Section */}
      <FormSection
        title="Event Videos"
        titleAs="h3"
        description={maxVideos ? `${videoIds.length} of ${maxVideos} videos used.` : undefined}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowVideoModal(true)}
            disabled={maxVideos ? videoIds.length >= maxVideos : false}
          >
            {videoIds.length > 0 ? 'Manage Videos' : 'Add Videos'}
          </Button>
        }
      >
        {/* Video Thumbnails */}
        {videoIds.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {videoIds.map((videoId, index) => {
              const mediaItem = media.find(m => m.id === videoId);
              return (
                <div key={videoId} className="relative group">
                  <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                    {mediaItem?.cloudinaryUrl ? (
                      <video
                        src={mediaItem.cloudinaryUrl}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  {/* Order indicator */}
                  <div className="absolute top-2 left-2 w-6 h-6 bg-primary-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {index + 1}
                  </div>
                  
                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => handleRemoveVideo(videoId)}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Remove video"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-gray-600">No videos added yet</p>
            <p className="text-xs text-gray-500 mt-1">Click "Add Videos" to select videos</p>
          </div>
        )}
      </FormSection>

      {/* Photo Picker Modal */}
      <MediaPickerModal
        isOpen={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        mode="multi-ordered"
        venueId={venueId}
        selectedIds={photoIds}
        onSelectionChange={onPhotosChange}
        title="Select Event Photos"
        maxSelection={maxPhotos}
        showUpload={true}
      />

      {/* Video Picker Modal */}
      <MediaPickerModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        mode="multi-ordered"
        venueId={venueId}
        selectedIds={videoIds}
        onSelectionChange={onVideosChange}
        title="Select Event Videos"
        maxSelection={maxVideos}
        showUpload={true}
      />
    </div>
  );
};
